import {
  SidebarPanelLayout,
  SidebarPanelHeader,
  SidebarPanelBody,
  SidebarPanelLeftSidebar,
  SidebarPanelContent,
} from "./SidebarPanelLayout";
import GradientTabButton from "@/lib/components/Button/GradientTabButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import { Button } from "@/lib/components/Button/Button";
import { cn } from "@/lib/utils";
import {
  IconArrowRight,
  IconClock,
  IconDots,
  IconEdit,
  IconEye,
  IconHeart,
  IconHeartFilled,
  IconTrash,
} from "@tabler/icons-react";
import { GoBack, MainViewState, ViewStateOf } from "./creative-project-hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { IconList } from "./IconList";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import {
  usePagedAnimatedImagePresetsQuery,
  useDeleteAnimatedImagePresetMutation,
  useSetAnimatedImagePresetFavouriteMutation,
} from "@/lib/queries/animated-image-preset-queries";
import {
  usePagedSourceImagePresetsQuery,
  useDeleteSourceImagePresetMutation,
  useSetSourceImagePresetFavouriteMutation,
} from "@/lib/queries/source-image-preset-queries";
import { UpdatePromptPresetDialog } from "./UpdatePromptPresetDialog";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import { AnimatedImagePresetDto } from "@repo/api/animated-image-preset";
import { SourceImagePresetDto } from "@repo/api/source-image-preset";
import { AnimatedImageCustomInput } from "@repo/api/animated-image";
import { SourceImageCustomInput } from "@repo/api/source-image";
import {
  applyAnimatedImageCustomInput,
  applySourceImageCustomInput,
} from "@/lib/stores/editor-store/creative-project-state";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import ImageBadge from "@/lib/components/ImageBadge";
import Toast from "@/lib/components/Toast";

type PromptLibraryTab = "FAVOURITES" | "RECENTLY_USED";

function PromptLibraryView({
  goBackMainView,
  mainView,
}: {
  goBackMainView: GoBack;
  mainView: ViewStateOf<MainViewState, "PROMPT_LIBRARY">;
}) {
  const source = mainView.source;
  const teamExternalId = useEditorStore((state) => state.teamExternalId);
  const [activeTab, setActiveTab] = useState<PromptLibraryTab>("RECENTLY_USED");
  const presetFilters = useMemo(
    () => (activeTab === "FAVOURITES" ? { isFavourite: true } : {}),
    [activeTab],
  );
  const {
    data: animatedPresetsData,
    isLoading: animatedPresetsIsLoading,
    isFetching: animatedIsFetching,
    hasNextPage: animatedHasNextPage,
    isFetchingNextPage: animatedIsFetchingNextPage,
    fetchNextPage: fetchNextAnimatedPage,
  } = usePagedAnimatedImagePresetsQuery(teamExternalId, presetFilters, 50);
  const {
    data: sourceImagePresetsData,
    isLoading: sourceImagePresetsIsLoading,
    isFetching: sourceIsFetching,
    hasNextPage: sourceHasNextPage,
    isFetchingNextPage: sourceIsFetchingNextPage,
    fetchNextPage: fetchNextSourcePage,
  } = usePagedSourceImagePresetsQuery(teamExternalId, presetFilters, 50);
  const animatedPresets = useMemo(
    () => animatedPresetsData?.pages.flatMap((page) => page.presets) ?? [],
    [animatedPresetsData],
  );
  const sourceImagePresets = useMemo(
    () => sourceImagePresetsData?.pages.flatMap((page) => page.presets) ?? [],
    [sourceImagePresetsData],
  );
  const { updateCurrentCreativeProjectState } = useEditorStore(
    (state) => state.service,
  );

  const deleteAnimatedPreset = useDeleteAnimatedImagePresetMutation({
    teamExternalId,
  });
  const deleteSourcePreset = useDeleteSourceImagePresetMutation({
    teamExternalId,
  });
  const setAnimatedFavourite = useSetAnimatedImagePresetFavouriteMutation({
    teamExternalId,
  });
  const setSourceFavourite = useSetSourceImagePresetFavouriteMutation({
    teamExternalId,
  });

  const handleDeletePrompt = (presetId: string) => {
    if (source === "ANIMATION") {
      deleteAnimatedPreset.mutate(presetId);
    } else {
      deleteSourcePreset.mutate(presetId);
    }
  };

  const handleToggleFavourite = (presetId: string, isFavourite: boolean) => {
    if (source === "ANIMATION") {
      setAnimatedFavourite.mutate({
        presetExternalId: presetId,
        isFavourite,
      });
    } else {
      setSourceFavourite.mutate({
        presetExternalId: presetId,
        isFavourite,
      });
    }
  };

  const handleUsePrompt = (item: PromptListItem) => {
    updateCurrentCreativeProjectState((draft) => {
      if (item.source === "ANIMATION") {
        applyAnimatedImageCustomInput(draft, item.customInput);
        return;
      }
      if (mainView.source === "EDIT_IMAGE") {
        applySourceImageCustomInput(
          draft,
          mainView.sourceImageKey,
          item.customInput,
        );
      }
    });
    goBackMainView();
  };

  const [editingPreset, setEditingPreset] = useState<
    AnimatedImagePresetDto | SourceImagePresetDto | null
  >(null);

  const handleEditPrompt = (presetId: string) => {
    const presets =
      source === "ANIMATION" ? animatedPresets : sourceImagePresets;
    const preset = presets.find((p) => p.externalId === presetId) ?? null;
    setEditingPreset(preset);
  };

  const title = source === "EDIT_IMAGE" ? "Image Prompts" : "Animation Prompts";

  const tabVariant = source === "EDIT_IMAGE" ? "tertiarySecondary" : "default";

  const prompts = useMemo<PromptListItem[]>(() => {
    if (source === "ANIMATION") {
      return animatedPresets.map<PromptListItem>((preset) => ({
        source: "ANIMATION",
        id: preset.externalId,
        name: preset.name,
        customInput: preset.customInput,
        isTeamPrompt: preset.teamExternalId !== null,
        isFavourite: preset.isFavourite,
        src: preset.startImageSrc ?? undefined,
        endImageSrc: preset.endImageSrc ?? undefined,
        previewVideoSrc: preset.previewVideoSrc ?? undefined,
      }));
    }

    return sourceImagePresets.map<PromptListItem>((preset) => ({
      source: "EDIT_IMAGE",
      id: preset.externalId,
      name: preset.name,
      customInput: preset.customInput,
      isTeamPrompt: preset.teamExternalId !== null,
      isFavourite: preset.isFavourite,
      src: preset.generatedSrc ?? undefined,
      originalSrc: preset.originalSrc ?? undefined,
      previewVideoSrc: undefined,
    }));
  }, [source, animatedPresets, sourceImagePresets]);

  const isLoading =
    source === "ANIMATION"
      ? animatedPresetsIsLoading
      : sourceImagePresetsIsLoading;

  const isFetching =
    source === "ANIMATION" ? animatedIsFetching : sourceIsFetching;

  const showLoadingSpinner = isLoading || (isFetching && prompts.length === 0);

  const hasNextPage =
    source === "ANIMATION" ? animatedHasNextPage : sourceHasNextPage;

  const isFetchingNextPage =
    source === "ANIMATION"
      ? animatedIsFetchingNextPage
      : sourceIsFetchingNextPage;

  const fetchNextPage =
    source === "ANIMATION" ? fetchNextAnimatedPage : fetchNextSourcePage;

  return (
    <SidebarPanelLayout>
      <SidebarPanelHeader title={title} onBackClick={() => goBackMainView()} />
      <SidebarPanelBody>
        <SidebarPanelLeftSidebar>
          <PromptLibraryTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            variant={tabVariant}
          />
        </SidebarPanelLeftSidebar>
        <SidebarPanelContent>
          {showLoadingSpinner ? (
            <div className="flex min-w-full flex-1 items-center justify-center">
              <LoadingSpinner width={24} height={24} />
            </div>
          ) : (
            <PromptList
              source={source}
              activeTab={activeTab}
              prompts={prompts}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onLoadMore={fetchNextPage}
              onUsePrompt={handleUsePrompt}
              onEditPrompt={handleEditPrompt}
              onDeletePrompt={handleDeletePrompt}
              onToggleFavourite={handleToggleFavourite}
            />
          )}
        </SidebarPanelContent>
      </SidebarPanelBody>
      {editingPreset && (
        <UpdatePromptPresetDialog
          open={!!editingPreset}
          onOpenChange={(isOpen) => {
            if (!isOpen) setEditingPreset(null);
          }}
          source={source}
          selectedPreset={
            editingPreset as AnimatedImagePresetDto & SourceImagePresetDto
          }
          teamExternalId={teamExternalId}
        />
      )}
    </SidebarPanelLayout>
  );
}

function PromptLibraryTabs({
  activeTab,
  onTabChange,
  variant,
}: {
  activeTab: PromptLibraryTab;
  onTabChange: (tab: PromptLibraryTab) => void;
  variant: "default" | "tertiarySecondary";
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-grey-200 p-6">
      <GradientTabButton
        icon={IconClock}
        label="Recently Used"
        isActive={activeTab === "RECENTLY_USED"}
        onClick={() => onTabChange("RECENTLY_USED")}
        variant={variant}
      />
      <GradientTabButton
        icon={IconHeart}
        label="Favourite Prompts"
        isActive={activeTab === "FAVOURITES"}
        onClick={() => onTabChange("FAVOURITES")}
        variant={variant}
      />
    </div>
  );
}

const ESTIMATED_PROMPT_ROW_HEIGHT = 80;

function PromptList({
  source,
  activeTab,
  prompts,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onUsePrompt,
  onEditPrompt,
  onDeletePrompt,
  onToggleFavourite,
}: {
  source: ViewStateOf<MainViewState, "PROMPT_LIBRARY">["source"];
  activeTab: PromptLibraryTab;
  prompts: PromptListItem[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  onUsePrompt: (item: PromptListItem) => void;
  onEditPrompt: (presetId: string) => void;
  onDeletePrompt: (presetId: string) => void;
  onToggleFavourite: (presetId: string, isFavourite: boolean) => void;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const totalRows = prompts.length;

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? totalRows + 1 : totalRows,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ESTIMATED_PROMPT_ROW_HEIGHT,
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem) return;

    if (lastItem.index >= totalRows - 1 && hasNextPage && !isFetchingNextPage) {
      onLoadMore();
    }
  }, [virtualItems, totalRows, hasNextPage, isFetchingNextPage, onLoadMore]);

  const content = (() => {
    if (prompts.length === 0) {
      const isFavourites = activeTab === "FAVOURITES";
      const isAnimation = source === "ANIMATION";

      const title = isAnimation
        ? isFavourites
          ? "No favourite animation prompts yet"
          : "No recently used animation prompts"
        : isFavourites
          ? "No favourite image prompts yet"
          : "No recently used image prompts";

      const description = isAnimation
        ? isFavourites
          ? "Mark animation prompts as favourite to quickly access them here."
          : "Animation prompts you have recently used will appear here."
        : isFavourites
          ? "Mark image prompts as favourite to quickly access them here."
          : "Image prompts you have recently used will appear here.";

      return (
        <div className="flex h-full flex-col items-center justify-center px-8 py-12">
          <span className="text-md mb-3 font-medium text-text-primary">
            {title}
          </span>
          <p className="text-center text-sm font-medium text-text-secondary">
            {description}
          </p>
        </div>
      );
    }

    return (
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {virtualItems.map((virtualRow) => {
          const isLoaderRow = virtualRow.index >= totalRows;
          const prompt = prompts[virtualRow.index];

          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {isLoaderRow ? (
                <div className="flex justify-center py-4">
                  {isFetchingNextPage && (
                    <LoadingSpinner width={20} height={20} />
                  )}
                </div>
              ) : prompt ? (
                <PromptListItem
                  source={source}
                  prompt={prompt}
                  activeTab={activeTab}
                  onUsePrompt={onUsePrompt}
                  onEditPrompt={onEditPrompt}
                  onDeletePrompt={onDeletePrompt}
                  onToggleFavourite={onToggleFavourite}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    );
  })();

  return (
    <div className="relative grow overflow-hidden rounded-br-xl">
      <div
        ref={scrollContainerRef}
        className="absolute inset-0 overflow-y-auto px-5 py-4 pb-8"
      >
        {content}
      </div>
      <div className="pointer-events-none absolute right-0 -bottom-1 left-0 h-16 bg-linear-to-t from-white" />
    </div>
  );
}

type PromptListItemCommon = {
  id: string;
  name: string;
  isTeamPrompt: boolean;
  isFavourite: boolean;
  src?: string;
  originalSrc?: string;
  endImageSrc?: string;
  previewVideoSrc?: string;
};

type PromptListItem =
  | (PromptListItemCommon & {
      source: "ANIMATION";
      customInput: AnimatedImageCustomInput;
    })
  | (PromptListItemCommon & {
      source: "EDIT_IMAGE";
      customInput: SourceImageCustomInput;
    });

function PromptListItem({
  source,
  prompt,
  activeTab,
  onUsePrompt,
  onEditPrompt,
  onDeletePrompt,
  onToggleFavourite,
}: {
  source: ViewStateOf<MainViewState, "PROMPT_LIBRARY">["source"];
  prompt: PromptListItem;
  activeTab: PromptLibraryTab;
  onUsePrompt: (item: PromptListItem) => void;
  onEditPrompt: (presetId: string) => void;
  onDeletePrompt: (presetId: string) => void;
  onToggleFavourite: (presetId: string, isFavourite: boolean) => void;
}) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isUnfavouriteDialogOpen, setIsUnfavouriteDialogOpen] = useState(false);

  const variant = source === "EDIT_IMAGE" ? "tertiary" : "default";

  return (
    <div className="group/row -mx-3 rounded-lg px-3 py-2.5">
      <div className="relative flex items-center">
        <div className="relative h-15 w-15 rounded-md">
          {prompt.src ? (
            prompt.previewVideoSrc || prompt.originalSrc ? (
              <button
                type="button"
                className="group relative h-full w-full cursor-pointer rounded-md"
                onClick={() => setIsPreviewOpen(true)}
              >
                <img
                  src={prompt.src}
                  className="h-full w-full rounded-md object-cover"
                />
                <div className="absolute inset-0 rounded-md transition-colors duration-200 group-hover:bg-black/50 group-hover/row:bg-black/10 group-focus-visible:bg-black/50 group-focus-visible:duration-initial" />
                <span className="absolute inset-0 flex items-center justify-center text-white opacity-0 transition-opacity duration-100 group-focus-within/row:opacity-100 group-hover:opacity-100 group-hover/row:opacity-100">
                  <IconEye size={16} />
                </span>
              </button>
            ) : (
              <div className="relative h-full w-full">
                <img
                  src={prompt.src}
                  className="h-full w-full rounded-md object-cover"
                />
              </div>
            )
          ) : (
            <div
              className={cn(
                "flex h-15 w-15 items-center justify-center rounded-md",
                variant === "tertiary"
                  ? "bg-tertiary-100"
                  : "[&_svg]:stroke-[url(#icon-gradient)]",
              )}
              style={
                variant === "default"
                  ? {
                      background:
                        "linear-gradient(to bottom right, var(--color-primary-100), var(--color-secondary-100))",
                    }
                  : undefined
              }
            >
              <IconList
                className={cn(
                  "size-6",
                  variant === "tertiary" && "text-tertiary-400",
                )}
              />
            </div>
          )}
        </div>
        <div className="ml-4 max-w-prose min-w-0 flex-1">
          <h3
            className={cn(
              "truncate text-base font-semibold",
              prompt.name ? "text-text-primary" : "text-text-primary/60",
            )}
          >
            {prompt.name || "Untitled Prompt"}
          </h3>
          <p className="mt-0.5 truncate text-sm text-text-secondary">
            {prompt.customInput.prompt}
          </p>
        </div>
        <div className="pointer-events-none absolute top-1/2 right-0 flex -translate-y-1/2 items-center justify-end gap-3 pl-4 opacity-0 group-focus-within/row:pointer-events-auto group-focus-within/row:static group-focus-within/row:translate-y-0 group-focus-within/row:opacity-100 group-hover/row:pointer-events-auto group-hover/row:static group-hover/row:translate-y-0 group-hover/row:opacity-100">
          {prompt.isTeamPrompt && (
            <>
              <FavouriteToggleButton
                isFavourite={prompt.isFavourite}
                onToggle={() => {
                  if (activeTab === "FAVOURITES" && prompt.isFavourite) {
                    setIsUnfavouriteDialogOpen(true);
                    return;
                  }
                  onToggleFavourite(prompt.id, !prompt.isFavourite);
                  Toast.info(
                    prompt.isFavourite
                      ? "Removed from favourites"
                      : "Added to favourites",
                  );
                }}
              />
              <PromptItemDropdownButton
                onEdit={() => onEditPrompt(prompt.id)}
                onDelete={() => onDeletePrompt(prompt.id)}
              />
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onUsePrompt(prompt)}
          >
            Use
          </Button>
        </div>
      </div>
      {prompt.previewVideoSrc && (
        <PromptPreviewDialog
          open={isPreviewOpen}
          onOpenChange={setIsPreviewOpen}
          prompt={prompt}
        />
      )}
      {!prompt.previewVideoSrc && prompt.originalSrc && (
        <SourceImagePreviewDialog
          open={isPreviewOpen}
          onOpenChange={setIsPreviewOpen}
          prompt={prompt}
        />
      )}
      <UnfavouritePromptDialog
        open={isUnfavouriteDialogOpen}
        onOpenChange={setIsUnfavouriteDialogOpen}
        promptName={prompt.name}
        onConfirm={() => {
          onToggleFavourite(prompt.id, false);
          Toast.info("Removed from favourites");
          setIsUnfavouriteDialogOpen(false);
        }}
      />
    </div>
  );
}

function PromptPreviewDialog({
  open,
  onOpenChange,
  prompt,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prompt: PromptListItem;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader className="w-full">
          <DialogTitle
            className={cn(
              "mb-2",
              prompt.name ? "text-text-primary" : "text-text-primary/60",
            )}
          >
            {prompt.name || "Untitled Prompt"}
          </DialogTitle>
          <DialogDescription>{prompt.customInput.prompt}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-6">
          <div className="flex flex-1 flex-col gap-4">
            {prompt.src && (
              <div className="relative">
                <img
                  src={prompt.src}
                  className="w-full rounded-md object-cover"
                  alt="Start image"
                />
                <ImageBadge>Start Image</ImageBadge>
              </div>
            )}
            {prompt.endImageSrc && (
              <div className="relative">
                <img
                  src={prompt.endImageSrc}
                  className="w-full rounded-md object-cover"
                  alt="End image"
                />
                <ImageBadge>End Image</ImageBadge>
              </div>
            )}
          </div>
          <div className="[&_svg]:stroke-[url(#icon-gradient)]">
            <IconArrowRight
              size={24}
              className="shrink-0 text-text-secondary"
            />
          </div>
          <div className="relative flex-1">
            {prompt.previewVideoSrc && (
              <video
                src={prompt.previewVideoSrc}
                className="w-full rounded-md object-cover"
                muted
                autoPlay
                loop
                playsInline
              />
            )}
            <ImageBadge>Animated Result</ImageBadge>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SourceImagePreviewDialog({
  open,
  onOpenChange,
  prompt,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prompt: PromptListItem;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader className="w-full">
          <DialogTitle>{prompt.name}</DialogTitle>
          <DialogDescription>{prompt.customInput.prompt}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-6">
          {prompt.originalSrc && (
            <div className="relative flex-1">
              <img
                src={prompt.originalSrc}
                className="w-full rounded-md object-cover"
                alt="Original image"
              />
              <ImageBadge>Original Image</ImageBadge>
            </div>
          )}
          <div className="[&_svg]:stroke-[url(#icon-gradient)]">
            <IconArrowRight
              size={24}
              className="shrink-0 text-text-secondary"
            />
          </div>
          {prompt.src && (
            <div className="relative flex-1">
              <img
                src={prompt.src}
                className="w-full rounded-md object-cover"
                alt="Result image"
              />
              <ImageBadge>Result</ImageBadge>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PromptItemDropdownButton({
  onEdit,
  onDelete,
}: {
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="iconSm">
          <IconDots size={18} className="text-text-primary" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-48"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <DropdownMenuItem onClick={onEdit}>
          <IconEdit size={18} />
          Edit prompt
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-error" onClick={onDelete}>
          <IconTrash size={18} />
          Delete prompt
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FavouriteToggleButton({
  isFavourite,
  onToggle,
}: {
  isFavourite: boolean;
  onToggle: () => void;
}) {
  return (
    <Button
      variant="outline"
      size="iconSm"
      onClick={(e) => {
        // Blur on mouse activation so the row doesn't stay visible after the
        // pointer leaves; keyboard activation (detail === 0) keeps focus.
        if (e.detail !== 0) {
          e.currentTarget.blur();
        }
        onToggle();
      }}
      aria-label={isFavourite ? "Remove favourite" : "Add to favourites"}
    >
      {isFavourite ? (
        <IconHeartFilled size={18} className="text-error" />
      ) : (
        <IconHeart size={18} className="text-text-primary" />
      )}
    </Button>
  );
}

function UnfavouritePromptDialog({
  open,
  onOpenChange,
  promptName,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  promptName: string;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="w-full">
          <DialogTitle>Remove Favourite</DialogTitle>
          <DialogDescription>
            Are you sure you want to remove{" "}
            <span
              className={cn(
                "font-semibold",
                promptName ? "text-text-primary" : "text-text-primary/60",
              )}
            >
              {promptName || "Untitled Prompt"}
            </span>{" "}
            from your favourites?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-0">
          <Button onClick={onConfirm}>Remove</Button>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PromptLibraryView;
